import { EmptystateSearchNoResultsColored, Magnify, Trash } from "components/icons/generated";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import EmptyState from "ds/components/EmptyState";
import Icon from "ds/components/Icon";
import Input from "ds/components/Input";
import ListItemAction from "ds/components/ListItemAction";
import LoadingIndicator from "ds/components/LoadingIndicator";
import TextEllipsis from "ds/components/TextEllipsis";
import Typography from "ds/components/Typography";

import { AiChatSession } from "../../types";
import useAiChatHistory from "../useHistory";
import styles from "./styles.module.css";

type AiChatHistorySidePanelProps = {
  deleteChat: (chatId: string) => void;
  chats: AiChatSession[];
  currentChatId: string;
  loading: boolean;
  onSelectChat: (chatId: string) => void;
};

const AiChatHistorySidePanel = ({
  deleteChat,
  chats,
  currentChatId,
  loading,
  onSelectChat,
}: AiChatHistorySidePanelProps) => {
  const { searchInput, setSearchInput, sections, noSearchResults } = useAiChatHistory(chats);

  return (
    <Box
      direction="column"
      fullHeight
      shrink="0"
      overflowHidden
      className={styles.historySidePanel}
    >
      <Box padding="x-large" shrink="0">
        <Typography tag="h2" variant="p-t7" color="primary">
          History
        </Typography>
      </Box>

      <Box direction="column" padding="0 x-large" shrink="0" relative>
        <Box className={styles.searchIcon} align="center" justify="center">
          <LoadingIndicator loading={loading} minimumDuration={200}>
            <Icon src={Magnify} noShrink />
          </LoadingIndicator>
        </Box>
        <Input
          aria-label="AI chat history"
          placeholder={SEARCH_PLACEHOLDERS.AI_CHAT_HISTORY}
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          className={styles.searchInput}
        />
      </Box>

      {noSearchResults && (
        <EmptyState title="No search results" icon={EmptystateSearchNoResultsColored} fullHeight />
      )}

      <Box direction="column" scrollable padding="medium x-large">
        {sections.map((section) => (
          <Box direction="column" padding="medium 0" key={section.label}>
            <Typography
              tag="span"
              variant="p-body4"
              color="secondary"
              transform="uppercase"
              margin="medium 0"
            >
              {section.label}
            </Typography>

            {section.chats.map((chat) => (
              <Box relative key={chat.id} className={styles.chatRow}>
                <ListItemAction
                  fullWidth
                  active={chat.id === currentChatId}
                  onClick={() => onSelectChat(chat.id)}
                  className={styles.sectionItem}
                >
                  <Box direction="row" align="center" gap="large" fullWidth justify="between">
                    <TextEllipsis tooltip={chat.name}>
                      {(props) => (
                        <Typography {...props} variant="p-body3" color="primary" tag="span">
                          {chat.name}
                        </Typography>
                      )}
                    </TextEllipsis>
                  </Box>
                </ListItemAction>
                <ButtonIcon
                  className={styles.deleteButton}
                  icon={Trash}
                  variant="ghostDanger"
                  size="small"
                  onPress={() => deleteChat(chat.id)}
                >
                  Delete
                </ButtonIcon>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default AiChatHistorySidePanel;
